
import React from 'react';
import { FilterState, UserPermissions, User, Notification } from '../types';
import { useDebouncedCallback } from '../hooks/useDebounce';
import { SearchBar } from './header/SearchBar';
import { FilterPanel } from './header/FilterPanel';
import { NotificationBell } from './header/NotificationBell';
import { ConnectionStatus } from './header/ConnectionStatus';
import { UserMenu } from './header/UserMenu';
import { HeaderActionMenu } from './header/HeaderActionMenu';

/** Re-export para compatibilidade com imports existentes */
export type { FilterState };

interface HeaderProps {
  search: string;
  onSearchChange: (val: string) => void;
  filters: FilterState;
  onFilterChange: (filters: FilterState) => void;
  onOpenSettings?: () => void;
  onOpenChangePassword?: () => void;
  onAddEmployee?: () => void;
  onOpenDashboard?: () => void;
  onOpenHR?: () => void;
  onOpenAbsences?: () => void;
  onDownloadEmployeesReport?: () => void;
  onDownloadAbsencesReport?: () => void;
  onLogout?: () => void;
  userPermissions?: UserPermissions;
  currentUser?: User | null;
  notifications?: Notification[];
  onDismissNotification?: (id: string) => void;
  onClearNotifications?: () => void;
  isSynced?: boolean;
  isDbConnected?: boolean;
  timeOffset?: number;
  zoomLevel: number;
  onZoomChange: (zoom: number) => void;
  currentCompany?: string | null;
  onOpenVersionInfo?: () => void;
}

/**
 * Barra superior do board — compõe subcomponentes de busca, filtros, status e perfil.
 */
export const Header: React.FC<HeaderProps> = ({
  search,
  onSearchChange,
  filters,
  onFilterChange,
  onOpenSettings,
  onOpenChangePassword,
  onAddEmployee,
  onOpenDashboard,
  onOpenHR,
  onOpenAbsences,
  onDownloadEmployeesReport,
  onDownloadAbsencesReport,
  onLogout,
  userPermissions,
  currentUser,
  notifications = [],
  onDismissNotification,
  onClearNotifications,
  isSynced = false,
  isDbConnected = false,
  timeOffset = 0,
  zoomLevel,
  onZoomChange,
  currentCompany,
  onOpenVersionInfo,
}) => {
  const debouncedSearchChange = useDebouncedCallback((value: string) => {
    onSearchChange(value);
  }, 200);

  return (
    <header className="flex items-center justify-between whitespace-nowrap border-b border-slate-200 px-4 py-1.5 bg-white/80 backdrop-blur-md shadow-sm z-40 h-[48px] relative">
      {currentCompany && (
        <div className="absolute left-1/2 -translate-x-1/2 flex justify-center items-center pointer-events-none hidden md:flex">
          <span className="text-[13px] font-bold text-slate-700 tracking-tight flex items-center gap-1.5">
            <span className="material-symbols-outlined !text-[16px] text-primary-accent">apartment</span>
            {currentCompany}
          </span>
        </div>
      )}

      <div className="flex items-center gap-5">
        <div className="flex items-center gap-2">
          <div className="size-7 text-white flex items-center justify-center bg-primary-accent rounded-full shadow-lg shadow-primary-accent/10">
            <span className="material-symbols-outlined !text-[16px]">corporate_fare</span>
          </div>
          <h2 className="text-sm font-semibold leading-none tracking-tight">
            <span className="text-primary-accent">Gestão</span>
          </h2>
        </div>

        <div className="hidden md:flex items-center gap-3">
          <SearchBar value={search} onChange={debouncedSearchChange} />

          <HeaderActionMenu
            userPermissions={userPermissions}
            onOpenDashboard={onOpenDashboard}
            onOpenHR={onOpenHR}
            onOpenAbsences={onOpenAbsences}
            onDownloadEmployeesReport={onDownloadEmployeesReport}
            onDownloadAbsencesReport={onDownloadAbsencesReport}
          />
        </div>
      </div>

      <div className="flex items-center gap-3">
        <div className="hidden md:flex gap-2.5 items-center relative">
          <button
            type="button"
            title="Versão e novidades do sistema"
            onClick={() => onOpenVersionInfo?.()}
            className="flex items-center justify-center size-7 rounded-full border shadow-inner transition-all hover:scale-105 cursor-pointer bg-amber-50/90 border-amber-200/60 text-amber-700 hover:bg-amber-100"
            aria-label="Versão e novidades do sistema"
          >
            <span className="material-symbols-outlined !text-[16px] drop-shadow-sm">trophy</span>
          </button>

          <ConnectionStatus
            isDbConnected={isDbConnected}
            isSynced={isSynced}
            timeOffset={timeOffset}
          />

          <FilterPanel filters={filters} onFilterChange={onFilterChange} />

          {onAddEmployee && (
          <button
            type="button"
            onClick={onAddEmployee}
            className="flex h-7 items-center justify-center rounded-lg bg-secondary-accent px-3 text-white text-[11px] font-semibold shadow-lg shadow-secondary-accent/20 hover:brightness-105 active:scale-95 transition-all"
          >
            <span className="material-symbols-outlined mr-1.5 !text-[14px]">person_add</span>
            Novo
          </button>
          )}
        </div>

        <div className="h-5 w-px bg-slate-200 hidden md:block mx-0.5" />

        <NotificationBell
          notifications={notifications}
          onDismiss={onDismissNotification}
          onClear={onClearNotifications}
        />

        <UserMenu
          currentUser={currentUser}
          userPermissions={userPermissions}
          zoomLevel={zoomLevel}
          onZoomChange={onZoomChange}
          onOpenSettings={onOpenSettings}
          onOpenChangePassword={onOpenChangePassword}
          onLogout={onLogout}
        />
      </div>
    </header>
  );
};
